import api from '@shared/services/api';
import { createReportConfig } from '@utils/reportHelper';

const DISMISSED_EMPLOYEES_REPORT_KEY = 'option-4';
const SALARIES_AND_BONUSES_REPORT_KEY = 'option-5';

function pad(num) {
    return String(num).padStart(2, '0');
}

function formatDateForApi(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        throw new Error('Invalid date value.');
    }
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDateBg(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

function normalizeToArray(data) {
    if (Array.isArray(data)) return data;
    if (data != null && typeof data === 'object') return [data];
    return [];
}

function dash(value) {
    if (value == null || value === '') return '-';
    return String(value);
}

function pickField(item, ...keys) {
    for (const key of keys) {
        const value = item?.[key];
        if (value != null && value !== '') return value;
    }
    return null;
}

function formatOptionalDateBg(value) {
    if (value == null || value === '') return '-';
    const formatted = formatDateBg(value);
    return formatted || dash(value);
}

function buildFromToReportPayload(formState) {
    return {
        fromDate: formatDateForApi(formState.fromDate),
        toDate: formatDateForApi(formState.toDate),
    };
}

function buildAsOfDateReportPayload(formState) {
    return {
        asOfDate: formatDateForApi(formState.forPeriodDate),
    };
}

function formatOptionalAmount(value) {
    if (value == null || value === '') return '-';
    return String(value);
}

function parseAmount(value) {
    if (value == null || value === '') return null;
    const parsed = Number.parseFloat(String(value).replace(',', '.'));
    return Number.isNaN(parsed) ? null : parsed;
}

function formatDifferenceCol1311(item) {
    const direct = pickField(item, 'differenceCol1311', 'col1311Difference', 'difference');
    if (direct != null) return dash(direct);

    const currentSalary = parseAmount(pickField(item, 'salary', 'currentSalary'));
    const previousSalary = parseAmount(pickField(item, 'oldSalary', 'previousSalary'));
    if (currentSalary != null && previousSalary != null) {
        return String(currentSalary - previousSalary);
    }

    return '-';
}

function createDismissedEmployeesPdfConfig(fromDate, toDate) {
    const fromDateText = formatDateBg(fromDate || new Date());
    const toDateText = formatDateBg(toDate || new Date());

    const baseConfig = createReportConfig(
        `Информация за служители с прекратено трудово правоотношение в периода ${fromDateText} - ${toDateText} год.`,
        [
            { key: 'idn', label: 'ЕГН', render: (item) => dash(item?.idn) },
            { key: 'surname', label: 'Фамилия', render: (item) => dash(item?.surname) },
            { key: 'firstname', label: 'Собствено име', render: (item) => dash(item?.firstname) },
            {
                key: 'documentDate',
                label: 'Дата на постъпване',
                render: (item) => formatOptionalDateBg(item?.documentDate),
            },
            {
                key: 'effectiveDateWithNumber',
                label: '№, дата на заповед за назначаване',
                render: (item) => dash(item?.effectiveDateWithNumber),
            },
            { key: 'typeContract', label: 'Вид тр.договор', render: (item) => dash(item?.typeContract) },
            { key: 'positionName', label: 'Длъжност', render: (item) => dash(item?.positionName) },
            { key: 'positionCode', label: 'Код длъжност', render: (item) => dash(item?.positionCode) },
            { key: 'orgUnitLevelName', label: 'SG код', render: (item) => dash(item?.orgUnitLevelName) },
            {
                key: 'breakDateContract',
                label: 'Дата на напускане',
                render: (item) => formatOptionalDateBg(item?.breakDateContract),
            },
            {
                key: 'dateOfTheDocumentAndNumber',
                label: '№, дата на заповед за прекратяване',
                render: (item) => dash(item?.dateOfTheDocumentAndNumber),
            },
            { key: 'reason', label: 'Основание по КТ за прекратяване', render: (item) => dash(item?.reason) },
            { key: 'newEmployerName', label: 'Нов работодател', render: (item) => dash(item?.newEmployerName) },
            {
                label: 'Прекратено трудово правоотношение в',
                children: [
                    {
                        key: 'regionalCenter',
                        label: 'Регионален център',
                        render: (item) => dash(pickField(item, 'regionalCenter', 'regionalCentre', 'regionCenter')),
                    },
                    {
                        key: 'financialCenter',
                        label: 'Финансов център / Клон / Бизнес център',
                        render: (item) => dash(pickField(
                            item,
                            'financialCenter',
                            'finCenter',
                            'branch',
                            'businessCenter',
                            'businessCentre',
                        )),
                    },
                    {
                        key: 'directorate',
                        label: 'Дирекция',
                        render: (item) => dash(pickField(item, 'directorate', 'directorateName')),
                    },
                    {
                        key: 'department',
                        label: 'Отдел',
                        render: (item) => dash(pickField(item, 'department', 'departmentName')),
                    },
                    {
                        key: 'division',
                        label: 'Звено',
                        render: (item) => dash(pickField(item, 'division', 'unit', 'unitName', 'zveno')),
                    },
                    {
                        key: 'group',
                        label: 'Група',
                        render: (item) => dash(pickField(item, 'group', 'groupName', 'grupa')),
                    },
                ],
            },
            {
                key: 'orgUnitName',
                label: 'Принадлежност към',
                render: (item) => dash(item?.orgUnitName),
            },
        ],
        'staff-movement-dismissed-employees-report',
        '',
        {
            rowNumberLabel: '№ ред',
            tableFontSize: '5',
            titleFontSize: '10',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

function createSalariesAndBonusesPdfConfig(asOfDate) {
    const dateText = formatDateBg(asOfDate || new Date());

    const baseConfig = createReportConfig(
        `Информация за Заплати и бонуси от ${dateText} г.`,
        [
            { key: 'surname', label: 'Фамилия', render: (item) => dash(item?.surname) },
            { key: 'name', label: 'Име', render: (item) => dash(item?.name) },
            { key: 'idn', label: 'ЕГН', render: (item) => dash(item?.idn) },
            { key: 'positionName', label: 'Длъжност', render: (item) => dash(item?.positionName) },
            {
                key: 'effectiveDate',
                label: 'Дата на постъпване',
                render: (item) => formatOptionalDateBg(item?.effectiveDate),
            },
            {
                key: 'comment',
                label: 'Коментар',
                render: (item) => dash(pickField(item, 'comment', 'note', 'remarks')),
            },
            {
                key: 'annualEvaluation2024',
                label: 'Годишна оценка 2024',
                render: (item) => dash(pickField(item, 'annualEvaluation2024', 'evaluation2024', 'yearEvaluation2024')),
            },
            {
                key: 'annualEvaluation2025',
                label: 'Годишна оценка 2025',
                render: (item) => dash(pickField(item, 'annualEvaluation2025', 'evaluation2025', 'yearEvaluation2025')),
            },
            {
                key: 'differenceCol1311',
                label: 'Разлика. Кол.13-Кол.11',
                render: (item) => formatDifferenceCol1311(item),
            },
            {
                label: 'Предходна заплата',
                children: [
                    {
                        key: 'oldSalaryDate',
                        label: 'от дата',
                        render: (item) => formatOptionalDateBg(item?.oldSalaryDate),
                    },
                    {
                        key: 'oldSalary',
                        label: 'сума (евро)',
                        render: (item) => formatOptionalAmount(item?.oldSalary),
                    },
                ],
            },
            {
                label: 'Текуща заплата',
                children: [
                    {
                        key: 'salaryDate',
                        label: 'от дата',
                        render: (item) => formatOptionalDateBg(item?.salaryDate),
                    },
                    {
                        key: 'salary',
                        label: 'сума (евро)',
                        render: (item) => formatOptionalAmount(item?.salary),
                    },
                ],
            },
            {
                key: 'oldBonus',
                label: 'Бонус 2024',
                render: (item) => formatOptionalAmount(pickField(item, 'oldBonus', 'bonus2024')),
            },
            {
                key: 'bonus',
                label: 'Бонус 2025',
                render: (item) => formatOptionalAmount(pickField(item, 'bonus', 'bonus2025')),
            },
        ],
        'staff-movement-salaries-and-bonuses-report',
        '',
        {
            tableFontSize: '5',
            titleFontSize: '10',
        },
    );

    return {
        ...baseConfig,
        compactTable: true,
    };
}

async function getEmpTermReport(payload) {
    const response = await api.post('/emp-move-report/emp-term', payload);
    return normalizeToArray(response.data);
}

async function getSalaryHistReport(payload) {
    const response = await api.post('/emp-move-report/salary-hist', payload);
    return normalizeToArray(response.data);
}

export function buildStaffMovementReportExecution(reportKey, formState) {
    if (reportKey === DISMISSED_EMPLOYEES_REPORT_KEY) {
        const payload = buildFromToReportPayload(formState);
        return {
            reportService: () => getEmpTermReport(payload),
            reportConfig: createDismissedEmployeesPdfConfig(formState.fromDate, formState.toDate),
        };
    }

    if (reportKey === SALARIES_AND_BONUSES_REPORT_KEY) {
        const payload = buildAsOfDateReportPayload(formState);
        return {
            reportService: () => getSalaryHistReport(payload),
            reportConfig: createSalariesAndBonusesPdfConfig(formState.forPeriodDate),
        };
    }

    throw new Error('Selected report is not implemented yet.');
}
